// Financials tab redesign — the Phoenix Income Pools row drill-down: the
// period's underlying recognized income events, as plain facts (source,
// obligation type, classification, pool, amounts). Explicitly NO narration —
// just the records behind the booked figures. View-only.

import {
  Button,
  Skeleton,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import { formatDate } from "../common/colorBand";
import { ResourceDrawer } from "../common/ResourceDrawer";
import { useIncomePoolPeriodEvents } from "@/hooks/portfolioAdmin/useIncomePools";
import { formatPoolPeriod } from "./IncomePoolsPanel";

const SOURCE_LABEL: Record<string, string> = {
  servicing_disbursement: "Servicing payment",
  tps_distribution: "TPS distribution",
  tps_collection_recovery: "Collection recovery",
  vfi_invoice: "Vendor invoice",
  first_payment: "First payment",
  first_payment_spread: "First payment (spread)",
};

export function PoolPeriodDrawer({
  loanId,
  periodStart,
  onClose,
}: {
  loanId: string;
  periodStart: string | null;
  onClose: () => void;
}) {
  const query = useIncomePoolPeriodEvents(loanId, periodStart);
  const events = query.data;

  return (
    <ResourceDrawer
      open={!!periodStart}
      onClose={onClose}
      title="Booked pool income"
      subtitle={periodStart ? formatPoolPeriod(periodStart) : undefined}
      width="xl"
      testId="pool-period-drawer"
    >
      {query.isLoading && (
        <div className="flex flex-col gap-space-2" data-testid="pool-period-loading">
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-full" />
        </div>
      )}
      {query.isError && (
        <div className="flex flex-col items-start gap-space-2" data-testid="pool-period-error">
          <p className="text-small text-status-error m-0">
            The period&apos;s events could not be loaded.
          </p>
          <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
            Retry
          </Button>
        </div>
      )}
      {events && (
        <Table data-testid="pool-period-events">
          <THead>
            <TR>
              <TH>Recognized</TH>
              <TH>Source</TH>
              <TH>Type</TH>
              <TH>Pool</TH>
              <TH className="text-right">Amount</TH>
            </TR>
          </THead>
          <TBody>
            {events.length === 0 && (
              <TR>
                <TD colSpan={5} className="text-gray-mid">
                  No events in this period.
                </TD>
              </TR>
            )}
            {events.map((e) => (
              <TR key={e.id} data-testid={`pool-event-${e.id}`}>
                <TD>{formatDate(e.recognizedAt)}</TD>
                <TD>{SOURCE_LABEL[e.sourceType] ?? e.sourceType}</TD>
                <TD>
                  {(e.obligationTypeSnapshot ?? e.classification).replaceAll("_", " ")}
                  <span className="block text-caption text-gray-mid">{e.classification}</span>
                </TD>
                <TD>{e.poolCode ?? "Unattributed"}</TD>
                <TD className="text-right tabular-nums">
                  {formatCurrency(e.receivedAmount, { maximumFractionDigits: 2 })}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </ResourceDrawer>
  );
}
